import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Atom, FlaskConical, Microscope, ArrowRight } from "lucide-react";

const SUBJECTS = {
  physics: {
    label: "Physics",
    icon: Atom,
    color: "bg-amber-500",
    text: "text-amber-700",
    tint: "bg-amber-50 border-amber-200",
    tag: "Forces · Optics · Circuits",
  },
  chemistry: {
    label: "Chemistry",
    icon: FlaskConical,
    color: "bg-fuchsia-500",
    text: "text-fuchsia-700",
    tint: "bg-fuchsia-50 border-fuchsia-200",
    tag: "Reactions · Acids · Molecules",
  },
  biology: {
    label: "Biology",
    icon: Microscope,
    color: "bg-emerald-500",
    text: "text-emerald-700",
    tint: "bg-emerald-50 border-emerald-200",
    tag: "Cells · Organs · DNA",
  },
};

const LabSelector = () => {
  const { subject } = useParams();
  const [experiments, setExperiments] = useState([]);
  const [gradeFilter, setGradeFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subject) {
      // Subject picker page — no need to fetch experiments
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const { data } = await api.get("/experiments", { params: { subject } });
      setExperiments(data);
      setLoading(false);
    })();
  }, [subject]);

  const filtered = gradeFilter === "all" ? experiments : experiments.filter((e) => e.grade === gradeFilter);

  if (!subject) {
    // /labs → show subject picker
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="font-outfit font-black text-4xl sm:text-5xl text-slate-900" data-testid="labs-title">Choose your lab</h1>
          <p className="mt-2 text-slate-600 text-lg">Three subjects. Twenty-plus experiments. One curious owl.</p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {Object.entries(SUBJECTS).map(([key, S]) => (
              <Link key={key} to={`/labs/${key}`} data-testid={`labs-choose-${key}`}>
                <div className={`group rounded-3xl border-2 ${S.tint} p-8 hover:shadow-xl hover:-translate-y-1 transition-all relative overflow-hidden h-64 flex flex-col justify-between`}>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${S.color} text-white`}>
                    <S.icon className="h-7 w-7" />
                  </div>
                  <div>
                    <div className="font-outfit font-black text-3xl text-slate-900">{S.label}</div>
                    <div className={`text-sm ${S.text} font-semibold mt-1`}>{S.tag}</div>
                    <div className="mt-4 flex items-center gap-2 text-slate-900 font-bold">
                      Explore <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </main>
      </div>
    );
  }

  const S = SUBJECTS[subject];
  if (!S) return <div className="p-10">Unknown subject.</div>;
  const Icon = S.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/labs" className="text-sm text-slate-500 hover:text-slate-900 font-mono">← all labs</Link>
        <div className="mt-4 flex items-center gap-4">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${S.color} text-white`}>
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-outfit font-black text-4xl text-slate-900 capitalize" data-testid={`catalog-title-${subject}`}>{S.label} Lab</h1>
            <p className="text-slate-500">{S.tag}</p>
          </div>
        </div>

        {/* Grade filter */}
        <div className="mt-8 flex flex-wrap gap-2">
          {["all", "IX", "X", "XI", "XII"].map((g) => (
            <button
              key={g}
              onClick={() => setGradeFilter(g)}
              data-testid={`grade-filter-${g}`}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                gradeFilter === g
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
              }`}
            >
              {g === "all" ? "All classes" : `Class ${g}`}
            </button>
          ))}
        </div>

        {/* Experiments grid */}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? (
            <div className="col-span-full text-slate-400 font-mono">Loading experiments…</div>
          ) : filtered.length === 0 ? (
            <div className="col-span-full text-slate-500">No experiments for this filter yet.</div>
          ) : filtered.map((e) => (
            <Link key={e.id} to={`/experiment/${e.id}`} data-testid={`exp-card-${e.id}`}>
              <div className="group rounded-2xl bg-white border border-slate-200 p-6 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1 transition-all h-full flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${S.tint} ${S.text} border`}>Class {e.grade}</span>
                </div>
                <div className="font-outfit font-bold text-lg text-slate-900 leading-tight">{e.title}</div>
                <div className="text-sm text-slate-500 mt-2 line-clamp-3 flex-1">{e.short}</div>
                <div className="mt-4 flex items-center gap-2 text-blue-600 font-bold text-sm">
                  Start <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default LabSelector;
